import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Paper, 
  Typography, 
  Button, 
  CircularProgress, 
  Alert,
  AppBar,
  Toolbar,
  IconButton,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  PlayArrow as PlayIcon,
  Settings as SettingsIcon
} from '@mui/icons-material';
import { AuthContext } from '../AuthContext';
import api from '../api/axios';
import flowStepsApi from '../api/flowStepsApi';
import BreadcrumbsNav from '../components/BreadcrumbsNav';
import VisualFlowEditor, { TestFlowDialog } from '../components/VisualFlowEditor';
import FlowErrorBoundary from '../components/FlowErrorBoundary';

const FlowDesignerPage = () => {
  const { companyId, projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  // Mobile responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  
  // State management
  const [project, setProject] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [testFlowOpen, setTestFlowOpen] = useState(false);
  const [flowData, setFlowData] = useState({
    steps: [],
    connections: [],
    variables: [],
    settings: {
      allowFileUpload: true,
      allowApiInput: false,
      maxFileSize: 20,
      supportedFileTypes: ['pdf', 'png', 'jpg', 'jpeg', 'xlsx', 'docx']
    }
  });

  // Available data for the flow editor
  const [availableVariables, setAvailableVariables] = useState([]);
  const [availableApiEndpoints, setAvailableApiEndpoints] = useState([]);
  const [availableIntents, setAvailableIntents] = useState([]);

  // Function to extract variables from flow steps
  const extractVariablesFromFlow = useCallback((flowData) => {
    const variables = new Set();
    
    if (!flowData?.steps) return [];
    
    flowData.steps.forEach(step => {
      const config = step.config || {};
      
      switch (step.type) {
        case 'data_processing':
          // Document OCR & Vision creates output variables (default: extractedText)
          const outputVar = config.outputVariable || 'extractedText';
          variables.add(outputVar);
          break;
          
        case 'ai_extraction':
          // AI Field Mapping creates variables from extraction fields
          if (config.extractionFields) {
            config.extractionFields.forEach(field => {
              if (field.variableName) {
                variables.add(field.variableName);
              }
            });
          }
          break;
          
        case 'enhanced_api_call':
          // API calls create variables from response mappings
          if (config.responseMappings) {
            config.responseMappings.forEach(mapping => {
              if (mapping.variableName) {
                variables.add(mapping.variableName);
              }
            });
          }
          break;
          
        case 'variable_operations':
          // Variable operations can create new variables
          if (config.operations) {
            config.operations.forEach(operation => {
              if (operation.targetVariable) {
                variables.add(operation.targetVariable);
              }
            });
          }
          break;
          
        case 'advanced_collect_input':
          // Collect input creates variables from form fields
          if (config.fields) {
            config.fields.forEach(field => {
              if (field.variableName || field.name) {
                variables.add(field.variableName || field.name);
              }
            });
          }
          
          // Check for data source variables in table configurations
          if (config.dataSource) {
            variables.add(config.dataSource);
          }
          
          // Check for barcode scanner output variable
          if (config.interfaceType === 'barcode_scanner' && config.outputVariable) {
            variables.add(config.outputVariable);
          }
          
          // Check for file upload output variable
          if (config.interfaceType === 'file_upload' && config.outputVariable) {
            variables.add(config.outputVariable);
          }
          
          // Check for table_builder output variable
          if (config.interfaceType === 'table_builder' && config.outputVariable) {
            variables.add(config.outputVariable);
          }
          
          // Check for general file upload configuration (when enabled on other interfaces)
          if (config.allowFileUpload && config.fileUploadOutputVariable) {
            variables.add(config.fileUploadOutputVariable);
          }
          
          // Check for header field output variables
          if (config.headerFields) {
            config.headerFields.forEach(field => {
              if (field.outputVariable) {
                variables.add(field.outputVariable);
              }
            });
          }
          break;
          
        case 'external_data_source':
          // External Data Source creates variables from response mapping
          if (config.responseMapping) {
            // Add the extracted data variable (default: 'externalData')
            if (config.responseMapping.extractedData) {
              variables.add(config.responseMapping.extractedData);
            }
            // Add the count variable (default: 'dataCount')
            if (config.responseMapping.totalRows) {
              variables.add(config.responseMapping.totalRows);
            }
          }
          // Also add the full response object variable
          variables.add('externalDataResult');
          break;
      }
    });
    
    // Add some common/default variables that are typically available
    variables.add('extractedText');
    variables.add('lineItems');
    variables.add('products');
    variables.add('extractedItems');
    variables.add('invoiceData');
    variables.add('companyName');
    variables.add('totalAmount');
    variables.add('invoiceDate');
    
    return Array.from(variables).sort();
  }, []);

  // Update available variables when flow data changes
  useEffect(() => {
    const extractedVariables = extractVariablesFromFlow(flowData);
    console.log('🔍 Updating available variables:', extractedVariables);
    setAvailableVariables(extractedVariables);
  }, [flowData, extractVariablesFromFlow]);

  // Load project and flow data
  const fetchProjectData = async () => {
    setLoading(true);
    setError('');
    try {
      // Fetch company info
      try {
        const companyRes = await api.get(`/companies/${companyId}`);
        setCompany(companyRes.data);
      } catch (companyErr) {
        console.log('Company endpoint not implemented, using generic data');
        setCompany({
          _id: companyId,
          name: `Company ${companyId.slice(-6)}`,
          primaryColor: '#667eea'
        });
      }

      // Fetch project details
      try {
        const projectRes = await api.get(`/companies/${companyId}/projects/${projectId}`);
        const projectData = projectRes.data;
        setProject(projectData);
        
        // Load flow data from FlowStep API
        try {
          const flowData = await flowStepsApi.getFlowData(projectId);
          
          // Ensure all steps have the enabled field (for backward compatibility)
          const normalizedSteps = (flowData.steps || []).map(step => ({
            ...step,
            enabled: step.enabled !== undefined ? step.enabled : true
          }));
          
          setFlowData({
            steps: normalizedSteps,
            connections: flowData.connections || [],
            variables: flowData.variables || [],
            settings: flowData.settings || {
              allowFileUpload: true,
              allowApiInput: false,
              maxFileSize: 20,
              supportedFileTypes: ['pdf', 'png', 'jpg', 'jpeg', 'xlsx', 'docx']
            }
          });
        } catch (flowError) {
          console.log('No flow data found, using default:', flowError);
          // Initialize with default flow data if no steps exist
          setFlowData({
            steps: [],
            connections: [],
            variables: [],
            settings: {
              allowFileUpload: true,
              allowApiInput: false,
              maxFileSize: 20,
              supportedFileTypes: ['pdf', 'png', 'jpg', 'jpeg', 'xlsx', 'docx']
            }
          });
        }
      } catch (projectErr) {
        console.log('Project endpoint not implemented, using generic data');
        setProject({
          _id: projectId,
          name: `Project ${projectId.slice(-6)}`,
          description: 'Project description not available',
          status: 'draft',
          isPublished: false,
          companyId,
          flow: {
            version: 1,
            steps: [],
            connections: [],
            variables: [],
            settings: {
              allowFileUpload: true,
              allowApiInput: false,
              maxFileSize: 20,
              supportedFileTypes: ['pdf', 'png', 'jpg', 'jpeg', 'xlsx', 'docx']
            }
          }
        });
      }

      // TODO: Load available API endpoints, variables, etc. from backend
      setAvailableApiEndpoints([
        {
          id: 'sample-api-1',
          name: 'Sample Data Validation API',
          method: 'POST',
          url: 'https://api.example.com/validate',
          description: 'Validates extracted data against business rules',
          headers: { 'Content-Type': 'application/json' },
          parameters: [
            { name: 'data', type: 'object', required: true, description: 'Data to validate' }
          ],
          responseFields: [
            { name: 'isValid', type: 'boolean', description: 'Whether data is valid' },
            { name: 'errors', type: 'array', description: 'List of validation errors' }
          ]
        }
      ]);

      setAvailableIntents([
        { name: 'greeting', description: 'User greetings and welcome messages' },
        { name: 'data_submission', description: 'When user submits data or files' },
        { name: 'help_request', description: 'User asking for help or instructions' },
        { name: 'error_handling', description: 'Handling errors and exceptions' }
      ]);

    } catch (err) {
      setError('Failed to load project data');
      console.error('Flow designer fetch error:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (companyId && projectId) {
      fetchProjectData();
    }
  }, [companyId, projectId]);

  // Save flow data to backend
  const handleSaveFlow = async () => {
    setSaving(true);
    setError('');
    try {
      const endStep = flowData.steps?.find(s => s.type === 'end');
      if (endStep) {
        console.log('💾 [SAVE] End step config being sent to backend:', {
          enableMultiTab: endStep.config?.enableMultiTab,
          tabCount: endStep.config?.tabs?.length || 0,
          tabLabels: endStep.config?.tabs?.map(t => t.label),
          configKeys: Object.keys(endStep.config || {})
        });
      }
      
      const updatedFlowData = await flowStepsApi.updateFlowData(projectId, {
        steps: flowData.steps,
        connections: flowData.connections,
        variables: flowData.variables,
        settings: flowData.settings
      });

      setFlowData(updatedFlowData);
      
      if (endStep) {
        const savedEndStep = updatedFlowData.steps?.find(s => s.type === 'end');
        console.log('✅ [SAVE] End step config in response:', {
          enableMultiTab: savedEndStep?.config?.enableMultiTab,
          tabCount: savedEndStep?.config?.tabs?.length || 0,
          tabLabels: savedEndStep?.config?.tabs?.map(t => t.label),
          configKeys: Object.keys(savedEndStep?.config || {})
        });
      }
      
      console.log('Flow saved successfully!');
      
    } catch (err) {
      console.error('Save flow error:', err);
      setError(err.response?.data?.message || 'Failed to save flow');
    }
    setSaving(false);
  };

  // Handle flow data changes from the editor
  const handleFlowDataChange = useCallback((updatedFlowData) => {
    console.log('🔄 [FlowDesignerPage] handleFlowDataChange called:', {
      hasSteps: !!updatedFlowData?.steps,
      stepsCount: updatedFlowData?.steps?.length,
      hasConnections: !!updatedFlowData?.connections,
      connectionsCount: updatedFlowData?.connections?.length,
      hasVariables: !!updatedFlowData?.variables,
      hasSettings: !!updatedFlowData?.settings
    });
    setFlowData(updatedFlowData);
  }, []);

  // Navigation handlers
  const handleBackToProject = () => {
    navigate(`/company/${companyId}/project/${projectId}`);
  };

  const handleTestFlow = () => {
    setTestFlowOpen(true);
  };

  if (loading) {
    return (
      <Box maxWidth={1200} mx="auto" mt={4} px={2}>
        <BreadcrumbsNav />
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress size={60} />
        </Box>
      </Box>
    );
  }

  if (!project) {
    return (
      <Box maxWidth={1200} mx="auto" mt={4} px={2}>
        <BreadcrumbsNav />
        <Alert severity="error">Project not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <AppBar position="static" elevation={1} sx={{ bgcolor: company?.primaryColor || '#667eea' }}>
        <Toolbar sx={{ minHeight: isMobile ? 56 : 64, py: isMobile ? 0.5 : 1 }}>
          <IconButton
            color="inherit"
            onClick={handleBackToProject}
            size={isMobile ? "small" : "medium"}
            sx={{ mr: isMobile ? 1 : 2 }}
          >
            <ArrowBackIcon fontSize={isMobile ? "small" : "medium"} />
          </IconButton>
          
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} component="div" noWrap>
              {isMobile ? project.name : `Flow Designer - ${project.name}`}
            </Typography>
            {!isMobile && (
              <Typography variant="body2" sx={{ opacity: 0.9 }} noWrap>
                {company?.name} • {project.description || 'No description'}
              </Typography>
            )}
          </Box>

          <Box display="flex" gap={isMobile ? 0.5 : 2} alignItems="center" flexWrap="nowrap">
            {!isMobile && (
              <Chip 
                label={project.status || 'draft'} 
                color={project.status === 'active' ? 'success' : 'default'}
                size="small"
                sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
              />
            )}
            
            <Button
              color="inherit"
              startIcon={isMobile ? null : <PlayIcon />}
              onClick={handleTestFlow}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                minWidth: isMobile ? 'auto' : 'inherit',
                px: isMobile ? 1 : 2
              }}
            >
              {isMobile ? <PlayIcon fontSize="small" /> : 'Test Flow'}
            </Button>
            
            <Button
              color="inherit"
              startIcon={isMobile ? null : (saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />)}
              onClick={handleSaveFlow}
              disabled={saving}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                bgcolor: 'rgba(255,255,255,0.1)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                minWidth: isMobile ? 'auto' : 'inherit',
                px: isMobile ? 1 : 2
              }}
            >
              {isMobile ? (saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon fontSize="small" />) : (saving ? 'Saving...' : 'Save Flow')}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Flow Editor */}
      <Box sx={{ 
        flexGrow: 1, 
        bgcolor: '#f5f5f5',
        height: 0, // Force flex child height constraint
        position: 'relative',
        overflow: 'hidden'
      }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              position: 'absolute',
              top: 8,
              left: 8,
              right: 8,
              zIndex: 1000,
              boxShadow: 2
            }}
          >
            {error}
          </Alert>
        )}
        <FlowErrorBoundary>
                      <VisualFlowEditor
              flowData={flowData}
              onFlowDataChange={handleFlowDataChange}
              availableVariables={availableVariables}
              availableApiEndpoints={availableApiEndpoints}
              availableIntents={availableIntents}
              projectId={projectId}
              companyId={companyId}
            />
        </FlowErrorBoundary>
      </Box>

      {/* Test Flow Dialog */}
      <TestFlowDialog
        open={testFlowOpen}
        onClose={() => setTestFlowOpen(false)}
        flowData={flowData}
        availableVariables={availableVariables}
        projectId={projectId}
      />
    </Box>
  );
};

export default FlowDesignerPage; 